import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import "./themeupdate.css";
import "../rules/rules.css";
import "./gameContent.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import {
  listQuestions,
  saveQuestion,
  deleteQuestion,
  importQuestionsCsv,
} from "../../functions/questionsApi";
import GamePageImageTab from "./GamePageImageTab";
import QuestionsTab from "./QuestionsTab";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

/** Tab ids keep old/admin/themeUpdate.php's #tab2 / #tab3 hashes. */
const TABS = [
  { id: "tab2", label: "Add Game Page Image" },
  { id: "tab3", label: "Add Questions" },
];

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const hashTab = location.hash.replace("#", "");
  const activeTab = TABS.some((t) => t.id === hashTab) ? hashTab : "tab2";

  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [tourOpen, setTourOpen] = useState(false);

  const reloadTheme = useCallback(() => {
    if (!currentTheme) return Promise.resolve();
    return dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    reloadTheme();
  }, [reloadTheme]);

  const reloadQuestions = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setIsLoadingQuestions(true);
    try {
      const rows = await listQuestions({
        themeName: currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
      });
      setQuestions(rows);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load questions.");
    } finally {
      setIsLoadingQuestions(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    reloadQuestions();
  }, [reloadQuestions]);

  useEffect(() => {
    if (!data) return undefined;
    if (!shouldAutoStartThemeUpdateTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  /** Runs one question mutation, then reloads the list. Rethrows so modals stay open on error. */
  const runQuestionAction = async (action, successText, fallbackError) => {
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await action();
      await reloadQuestions();
      setSuccessMessage(successText);
    } catch (err) {
      setErrorMessage(err?.message || fallbackError);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuestion = ({ action, editId, question }) =>
    runQuestionAction(
      () => saveQuestion({ themeName: currentTheme, token: adminToken, action, editId, question }),
      "Data Updated",
      "Failed to save question."
    );

  const handleDeleteQuestion = (id) =>
    runQuestionAction(
      () => deleteQuestion({ themeName: currentTheme, id, token: adminToken }),
      "Question deleted.",
      "Failed to delete question."
    ).catch(() => {});

  const handleImportCsv = (rows) =>
    runQuestionAction(
      () => importQuestionsCsv({ themeName: currentTheme, rows, token: adminToken }),
      `Successfully updated all fields (${rows.length} questions).`,
      "Failed to import CSV."
    );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <nav className="se-tabs" data-tour="tour-gc-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab-outline se-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => navigate({ hash: `#${tab.id}` }, { replace: true })}
          >
            <i className="fa-solid fa-square-plus" aria-hidden /> {tab.label}
          </button>
        ))}
      </nav>

      <div data-tour="tour-gc-content">
        {activeTab === "tab2" ? (
          <GamePageImageTab
            themeData={data}
            currentTheme={currentTheme}
            adminToken={adminToken}
            onUploaded={reloadTheme}
          />
        ) : (
          <QuestionsTab
            questions={questions}
            isLoading={isLoadingQuestions}
            isSaving={isSaving}
            onSaveQuestion={handleSaveQuestion}
            onDeleteQuestion={handleDeleteQuestion}
            onImportCsv={handleImportCsv}
            errorMessage={errorMessage}
            successMessage={successMessage}
            setErrorMessage={setErrorMessage}
            setSuccessMessage={setSuccessMessage}
          />
        )}
      </div>

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
